import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

interface Page {
  pageNumber: number;
  content: string;
  imageUrl: string;
}

// 模拟数据
const mockStory = {
  id: '1',
  title: '小星星的奇妙冒险',
  summary: '一个关于友谊和勇气的温馨故事',
  pages: [
    {
      pageNumber: 1,
      content: '从前，有一个叫小星星的小朋友。他有一双明亮的大眼睛，总是充满好奇心。',
      imageUrl: 'https://via.placeholder.com/800x600/FFE4E1/FF69B4?text=Page+1',
    },
    {
      pageNumber: 2,
      content: '一天，小星星在森林里散步，遇到了一只迷路的小兔子。',
      imageUrl: 'https://via.placeholder.com/800x600/E6E6FA/9370DB?text=Page+2',
    },
    {
      pageNumber: 3,
      content: '"你愿意和我做朋友吗？"小兔子怯生生地问。',
      imageUrl: 'https://via.placeholder.com/800x600/F0FFF0/32CD32?text=Page+3',
    },
    {
      pageNumber: 4,
      content: '"当然！"小星星开心地说，"让我们一起找回家的路吧！"',
      imageUrl: 'https://via.placeholder.com/800x600/FFF8DC/DAA520?text=Page+4',
    },
    {
      pageNumber: 5,
      content: '他们一起翻过山丘，跨过小溪，经历了许多有趣的冒险。',
      imageUrl: 'https://via.placeholder.com/800x600/E0FFFF/00CED1?text=Page+5',
    },
    {
      pageNumber: 6,
      content: '终于，在太阳下山之前，他们找到了小兔子的家。',
      imageUrl: 'https://via.placeholder.com/800x600/FFE4B5/FF8C00?text=Page+6',
    },
    {
      pageNumber: 7,
      content: '"谢谢你，小星星！"小兔子感激地说，"你是最棒的朋友！"',
      imageUrl: 'https://via.placeholder.com/800x600/F5F5DC/8B4513?text=Page+7',
    },
    {
      pageNumber: 8,
      content: '小星星笑着说："我们是永远的朋友！下次再一起冒险吧！"',
      imageUrl: 'https://via.placeholder.com/800x600/FFF0F5/DB7093?text=Page+8',
    },
  ] as Page[],
};

function StoryViewer() {
  const { storyId } = useParams<{ storyId: string }>();
  // TODO: 实际应从API获取故事详情
  void storyId;
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const story = mockStory; // 实际应该从API获取
  const totalPages = story.pages.length;

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePlayAudio = () => {
    setIsPlaying(!isPlaying);
    // 实际应该播放音频
    if (!isPlaying) {
      console.log('播放音频:', story.pages[currentPage].content);
      setTimeout(() => setIsPlaying(false), 3000);
    }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-in">
      {/* 头部导航 */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/library')}
          className="flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-colors"
        >
          <span>←</span> 返回绘本库
        </button>
        <h1 className="text-2xl font-bold text-gray-800">{story.title}</h1>
        <div className="flex gap-2">
          <button className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors">
            ⬇️ 下载PDF
          </button>
        </div>
      </div>

      {/* 绘本阅读器 */}
      <div className="glass rounded-2xl p-8">
        {/* 进度条 */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">
              第 {currentPage + 1} / {totalPages} 页
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(((currentPage + 1) / totalPages) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-600 to-pink-600 transition-all duration-300"
              style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
            />
          </div>
        </div>

        {/* 绘本内容 */}
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* 左侧图片 */}
          <div className="relative aspect-[4/3] bg-gray-100 rounded-2xl overflow-hidden shadow-lg">
            <img
              src={story.pages[currentPage].imageUrl}
              alt={`第${currentPage + 1}页插图`}
              className="w-full h-full object-cover"
            />
            <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 rounded-full text-sm font-medium text-gray-700">
              第 {currentPage + 1} 页
            </div>
          </div>

          {/* 右侧文字 */}
          <div className="space-y-6">
            <div className="min-h-[200px] flex items-center">
              <p className="text-2xl leading-relaxed text-gray-800 font-medium">
                {story.pages[currentPage].content}
              </p>
            </div>

            {/* 播放按钮 */}
            <button
              onClick={handlePlayAudio}
              className={`
                flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all
                ${isPlaying
                  ? 'bg-pink-100 text-pink-700 animate-pulse'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }
              `}
            >
              {isPlaying ? (
                <>
                  <span className="animate-pulse">🔊</span>
                  朗读中...
                </>
              ) : (
                <>
                  <span>🔊</span>
                  朗读本页
                </>
              )}
            </button>
          </div>
        </div>

        {/* 翻页控制 */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all
              ${currentPage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg'
              }
            `}
          >
            ← 上一页
          </button>

          {/* 页码指示器 */}
          <div className="flex items-center gap-2">
            {story.pages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentPage(index)}
                className={`
                  w-3 h-3 rounded-full transition-all
                  ${index === currentPage
                    ? 'bg-purple-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                  }
                `}
              />
            ))}
          </div>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all
              ${currentPage === totalPages - 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50 shadow-md hover:shadow-lg'
              }
            `}
          >
            下一页 →
          </button>
        </div>

        {/* 键盘提示 */}
        <div className="mt-6 text-center text-sm text-gray-400">
          提示：可以使用键盘 ← → 方向键翻页
        </div>
      </div>

      {/* 故事信息 */}
      <div className="mt-8 glass rounded-2xl p-6">
        <h3 className="font-bold text-gray-800 mb-2">📖 故事简介</h3>
        <p className="text-gray-600">{story.summary}</p>
        <div className="mt-4 flex gap-4 text-sm text-gray-500">
          <span>📄 共 {totalPages} 页</span>
          <span>⏱️ 预计阅读 5 分钟</span>
          <span>👶 适合 5-7 岁</span>
        </div>
      </div>
    </div>
  );
}

export default StoryViewer;
